import { useMemo, useState } from "react";

import { Button } from "atoms/button/Button";
import { TextButton } from "atoms/text-button/TextButton";

import { useTranslation } from "@/core/i18n";

import { PersonalisationHeading } from "./PersonalisationHeading";
import { PersonalisationProgressBar } from "./PersonalisationProgressBar";
import { PersonalisationScreenLayout } from "./PersonalisationScreenLayout";
import { SingleChoiceTagPicker } from "./SingleChoiceTagPicker";

interface SkinColorItem {
  id: string;
  label: string;
}

interface SkinColorProps {
  options?: SkinColorItem[];
  onContinue: (selection: string) => void;
  onSkip?: () => void;
  selectedColor?: string;
  continueButtonText?: string;
  skipText?: string;
  progressPercentage: number;
  onBack?: () => void;
  onClose?: () => void;
  initialSelectedId?: string | null;
}

export const SkinColor = ({
  options,
  onContinue,
  onSkip,
  selectedColor,
  continueButtonText,
  skipText,
  progressPercentage,
  onBack,
  onClose,
  initialSelectedId,
}: SkinColorProps) => {
  const { t } = useTranslation();
  const defaultSkinTypeOptions = useMemo<SkinColorItem[]>(
    () => [
      { id: "very-fair", label: t("skinColor.optionVeryFair") },
      { id: "fair-light", label: t("skinColor.optionFairLight") },
      { id: "medium-olive", label: t("skinColor.optionMediumOlive") },
      { id: "tan-light-brown", label: t("skinColor.optionTanLightBrown") },
      { id: "brown", label: t("skinColor.optionBrown") },
      { id: "deep-brown-black", label: t("skinColor.optionDeepBrownBlack") },
    ],
    [t],
  );
  const resolvedOptions = options ?? defaultSkinTypeOptions;
  const resolvedContinueButtonText = continueButtonText ?? t("common.continue");
  const resolvedSkipText = skipText ?? t("skinColor.skipForNow");
  const [selected, setSelected] = useState<SkinColorItem | null>(
    initialSelectedId ? (resolvedOptions.find((o) => o.id === initialSelectedId) ?? null) : null,
  );

  const handleSelectionChange = (item: SkinColorItem | null) => {
    setSelected(item);
    onContinue?.(item?.id ?? "");
  };

  const handleContinue = () => {
    if (!selected) {
      return;
    }
    onContinue(selected.id);
  };

  const handleSkip = () => {
    onSkip?.();
  };

  const handleBack = () => {
    onBack?.();
  };

  const handleClose = () => {
    onClose?.();
  };

  const isContinueDisabled = selected === null;

  return (
    <PersonalisationScreenLayout
      header={
        <PersonalisationProgressBar
          progressPercentage={progressPercentage}
          onBack={handleBack}
          onClose={handleClose}
        />
      }
      footer={
        <>
          <Button
            style="primary"
            size="large"
            fullWidth
            disabled={isContinueDisabled}
            onClick={handleContinue}
          >
            {resolvedContinueButtonText}
          </Button>

          <TextButton variant="secondary" onClick={handleSkip}>
            {resolvedSkipText}
          </TextButton>
        </>
      }
    >
      <PersonalisationHeading size="sm">{t("skinColor.heading")}</PersonalisationHeading>

      <SingleChoiceTagPicker
        items={resolvedOptions}
        selectedId={selected?.id ?? null}
        onSelectionChangeRequest={handleSelectionChange}
        selectedColor={selectedColor}
        ariaLabel={t("skinColor.ariaLabel")}
      />
    </PersonalisationScreenLayout>
  );
};
